// Desktop (browser) notifications (SEARCH-NOTIF, 06 §Notification preferences). The server
// decides which inbox items ask for one — `inbox_item.desktop`: the person's preference for the
// kind, outside their quiet hours, when the item arrived — so this only shows them: while the
// browser allows notifications, an unread item that appears after the page loaded and asks for
// one becomes a notification (only when this window is not in front). It reads the unread inbox,
// the query the rail's count already uses, which change pings refresh (RT-01); a slow poll covers
// a stream that is down.
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import type { Api } from "../api/client";
import type { CompanyAccess } from "../features/company";
import { notificationPrefsQuery } from "../features/notifications/notification-queries";
import { inboxQuery } from "../features/work/work-queries";

const granted = () =>
  typeof window !== "undefined" &&
  "Notification" in window &&
  window.Notification.permission === "granted";

export function DesktopNotifier({ api, company }: { api: Api; company: CompanyAccess }) {
  const on = granted();
  const unread = useQuery({
    ...inboxQuery(api, company.id, "unread"),
    enabled: on,
    refetchInterval: 60_000,
  });
  // INBOX-PREFS: the pause holds alerts now, whatever the item asked for when it arrived.
  const prefs = useQuery({
    ...notificationPrefsQuery(api, company.id),
    enabled: on,
    refetchInterval: 60_000,
  });
  const pausedUntil = prefs.data?.paused_until ?? null;
  /** Items present at load (or already shown): never notified. A nudge is a new identity. */
  const seen = useRef<Set<string> | null>(null);
  useEffect(() => {
    const rows = unread.data?.data;
    if (!rows) return;
    const key = (r: (typeof rows)[number]) => `${r.id}:${r.nudged_at ?? ""}`;
    if (seen.current === null) {
      seen.current = new Set(rows.map(key));
      return;
    }
    const paused = pausedUntil !== null && new Date(pausedUntil).getTime() > Date.now();
    for (const r of rows) {
      if (seen.current.has(key(r))) continue;
      seen.current.add(key(r));
      if (paused || !r.desktop || r.read_at || !granted() || document.hasFocus()) continue;
      try {
        new window.Notification(company.name, { body: r.summary, tag: r.id });
      } catch {
        // A browser that refuses notifications outside a service worker: nothing to show.
      }
    }
  }, [unread.data, company.name, pausedUntil]);
  return null;
}
